import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../useApi";
import { useProject } from "../projectContext";
import type { Project, ProjectGitRepo } from "../types";

function AutoExpandTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const adjust = () => {
    const el = ref.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };
  useEffect(adjust, [props.value]);
  return (
    <textarea
      {...props}
      ref={ref}
      onInput={(e) => { adjust(); props.onInput?.(e); }}
      style={{ ...props.style, overflow: "hidden", resize: "none" }}
    />
  );
}

export default function ProjectSetupPage() {
  const { project: summary, refreshProjects } = useProject();
  const { call } = useApi();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    call<Project>(`/api/projects/${summary.projectId}`)
      .then(setProject)
      .catch((e: Error) => setError(e.message));
  }, [call, summary.projectId]);

  if (error) return <p className="error">{error}</p>;
  if (!project) return <p>Loading…</p>;

  const readOnly = summary.permission === "reader";

  const update = (patch: Partial<Project>) => setProject({ ...project, ...patch });
  const setRepo = (idx: number, patch: Partial<ProjectGitRepo>) => {
    const next = [...(project.gitRepos ?? [])];
    next[idx] = { ...next[idx], ...patch };
    update({ gitRepos: next });
  };
  const addRepo = () => update({ gitRepos: [...(project.gitRepos ?? []), { uri: "" }] });
  const removeRepo = (idx: number) => {
    const next = [...(project.gitRepos ?? [])];
    next.splice(idx, 1);
    update({ gitRepos: next });
  };

  const save = async () => {
    setSaving(true);
    setMessage(undefined);
    setError(undefined);
    try {
      const updated = await call<Project>(`/api/projects/${summary.projectId}`, {
        method: "PUT",
        body: JSON.stringify(project),
      });
      setProject(updated);
      setMessage("Project saved.");
      await refreshProjects();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async () => {
    if (!confirm(`Delete project "${project.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    setError(undefined);
    try {
      await call(`/api/projects/${summary.projectId}`, { method: "DELETE" });
      navigate("/");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>Project Setup</h1>
        <p>Configure Jira, Git repositories, the project prompt and execution limits.</p>
      </div>

      <div className="card">
        <h2>Jira</h2>
        <div className="grid-2">
          <div className="field">
            <label>Jira project key</label>
            <input
              value={project.jiraProjectKey}
              disabled={summary.permission !== "administrator"}
              onChange={(e) => update({ jiraProjectKey: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Jira base URL</label>
            <input
              value={project.jiraBaseUrl}
              disabled={readOnly}
              onChange={(e) => update({ jiraBaseUrl: e.target.value })}
              placeholder="https://vaisala.atlassian.net"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Git repositories</h2>
        {(project.gitRepos ?? []).map((repo, idx) => (
          <div key={idx} className="repo-row">
            <div className="grid-3">
              <div className="field">
                <label>URI</label>
                <input
                  value={repo.uri}
                  disabled={readOnly}
                  onChange={(e) => setRepo(idx, { uri: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Username</label>
                <input
                  value={repo.username ?? ""}
                  disabled={readOnly}
                  onChange={(e) => setRepo(idx, { username: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Password / PAT</label>
                <input
                  type="password"
                  value={repo.password ?? ""}
                  disabled={readOnly}
                  onChange={(e) => setRepo(idx, { password: e.target.value })}
                />
              </div>
            </div>
            {!readOnly && (
              <div className="row-actions">
                <button className="secondary" onClick={() => removeRepo(idx)}>Remove</button>
              </div>
            )}
          </div>
        ))}
        {!readOnly && (
          <button className="secondary" onClick={addRepo}>+ Add repository</button>
        )}
      </div>

      <div className="card">
        <h2>Limits</h2>
        <div className="grid-3">
          <div className="field">
            <label>Max executions per Jira issue</label>
            <input
              type="number"
              min={1}
              value={project.limits.maxExecutionsPerIssue}
              disabled={readOnly}
              onChange={(e) => update({ limits: { ...project.limits, maxExecutionsPerIssue: Number(e.target.value) } })}
            />
          </div>
          <div className="field">
            <label>Max descendant issues per epic</label>
            <input
              type="number"
              min={1}
              value={project.limits.maxDescendantIssuesPerEpic}
              disabled={readOnly}
              onChange={(e) => update({ limits: { ...project.limits, maxDescendantIssuesPerEpic: Number(e.target.value) } })}
            />
          </div>
          <div className="field">
            <label>Max spend per epic (USD)</label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={project.limits.maxEpicSpendUSD}
              disabled={readOnly}
              onChange={(e) => update({ limits: { ...project.limits, maxEpicSpendUSD: Number(e.target.value) } })}
            />
          </div>
          <div className="field">
            <label>Max execution time (minutes)</label>
            <input
              type="number"
              min={1}
              value={project.limits.maxExecutionTimeMinutes}
              disabled={readOnly}
              onChange={(e) => update({ limits: { ...project.limits, maxExecutionTimeMinutes: Number(e.target.value) } })}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Project prompt</h2>
        <p className="muted">Markdown text describing the layout and conventions of the project's repositories.</p>
        <AutoExpandTextarea
          value={project.projectPrompt ?? ""}
          disabled={readOnly}
          onChange={(e) => update({ projectPrompt: e.target.value })}
        />
      </div>

      {!readOnly && (
        <div className="card actions">
          {error && <span className="error" style={{ marginRight: 12 }}>{error}</span>}
          {message && <span style={{ marginRight: 12 }}>{message}</span>}
          <button disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
        </div>
      )}
      {summary.permission === "administrator" && (
        <div className="card">
          <h2>Danger zone</h2>
          <p>Deleting a project removes all project configuration and member access. Task and execution history is not deleted.</p>
          <div className="actions">
            <button className="secondary danger" disabled={deleting} onClick={deleteProject}>
              {deleting ? "Deleting…" : "Delete project"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
